import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { exportElementToPDF } from "@/lib/pdfExport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, Calendar, FileDown, RefreshCw, Video, ExternalLink } from "lucide-react";

const API = "/api";

const STATUS_OPTIONS = ["Present", "Absent", "Cancelled", "No Show"];

const getStatusStyle = (status) => {
  switch (status) {
    case "Present":
      return "bg-green-50 text-green-800 border-green-200";
    case "Absent":
      return "bg-red-50 text-red-800 border-red-200";
    case "Cancelled":
      return "bg-yellow-50 text-yellow-800 border-yellow-200";
    case "No Show":
      return "bg-rose-50 text-rose-800 border-rose-200";
    case "Subscription Renewed":
      return "bg-indigo-50 text-indigo-800 border-indigo-200";
    default:
      return "bg-slate-50 text-slate-800 border-slate-200";
  }
};

function ZoomLinkCard({ student, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [link, setLink] = useState(student.zoom_link || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/students/${student.id}`, { zoom_link: link.trim() });
      toast.success("Zoom link saved");
      setEditing(false);
      onUpdate();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Zoom Classroom Link</p>
            {editing ? (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  className="h-8 text-sm"
                  data-testid="zoom-link-input"
                />
                <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLink(student.zoom_link || ""); }} className="h-8">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-0.5">
                {student.zoom_link ? (
                  <>
                    <a href={student.zoom_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-md flex items-center gap-1">
                      {student.zoom_link} <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                ) : (
                  <span className="text-sm text-slate-400">Not set (using global link)</span>
                )}
              </div>
            )}
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="h-8 text-xs" data-testid="edit-zoom-btn">
              <Pencil className="w-3 h-3 mr-1" /> {student.zoom_link ? "Edit" : "Set Link"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const { getAuthHeader } = useAuth();
  const pageRef = useRef(null);
  const [student, setStudent] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newRecord, setNewRecord] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    status: "Present",
    notes: "",
  });
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewData, setRenewData] = useState({ additional_classes: 20, notes: "" });
  const [renewLoading, setRenewLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState("current");

  const fetchData = useCallback(async () => {
    try {
      const cfg = { headers: getAuthHeader() };
      const [studentRes, recordsRes] = await Promise.all([
        axios.get(`${API}/students/${id}`, cfg),
        axios.get(`${API}/students/${id}/records?limit=500`, cfg),
      ]);
      setStudent(studentRes.data);
      const recData = recordsRes.data;
      setRecords(Array.isArray(recData) ? recData : recData.items || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load student data");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddRecord = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/students/${id}/records`, newRecord);
      toast.success("Class record added");
      setAddDialogOpen(false);
      setNewRecord({
        date: format(new Date(), "yyyy-MM-dd"),
        status: "Present",
        notes: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to add record:", error);
      toast.error("Failed to add record");
    }
  };

  const handleStartEdit = (record) => {
    setEditingId(record.id);
    setEditData({
      date: record.date,
      status: record.status,
      notes: record.notes,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (recordId) => {
    try {
      await axios.put(`${API}/students/${id}/records/${recordId}`, editData);
      toast.success("Record updated");
      setEditingId(null);
      setEditData({});
      fetchData();
    } catch (error) {
      console.error("Failed to update record:", error);
      toast.error("Failed to update record");
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    try {
      await axios.delete(`${API}/students/${id}/records/${selectedRecord.id}`);
      toast.success("Record deleted");
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      fetchData();
    } catch (error) {
      console.error("Failed to delete record:", error);
      toast.error("Failed to delete record");
    }
  };

  // Export student details to PDF - captures the exact UI
  const handleExportPDF = () => {
    setExportType("current");
    setExportDialogOpen(true);
  };

  const doExportPDF = async () => {
    if (!pageRef.current) return;
    setExportDialogOpen(false);
    toast.info("Generating PDF...");
    
    // Save original records
    const allRecords = [...records];
    
    try {
      if (exportType === "current") {
        // Find the latest "Subscription Renewed" record date
        const renewalRecord = allRecords.find(r => r.status === "Subscription Renewed");
        if (renewalRecord) {
          // Keep only records from the renewal date onwards (excluding the renewal record itself from class list)
          const renewalDate = renewalRecord.date;
          const filtered = allRecords.filter(r => r.date >= renewalDate);
          setRecords(filtered);
        }
        // If no renewal found, keep all records (student never renewed)
      }
      // For "complete", keep all records as-is
      
      // Wait for React to re-render with filtered records
      await new Promise(r => setTimeout(r, 300));
      
      // Capture the page as PDF
      const typeLabel = exportType === "current" ? "current" : "complete";
      await exportElementToPDF(
        pageRef.current,
        `student_${student?.name?.replace(/\s+/g, '_') || 'report'}_${typeLabel}_${format(new Date(), 'yyyy-MM-dd')}.pdf`
      );
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF export failed:", error);
      toast.error("Failed to export PDF");
    } finally {
      // Always restore original records
      setRecords(allRecords);
    }
  };

  // Renew subscription - add credits
  const handleRenewSubscription = async () => {
    if (!renewData.additional_classes || renewData.additional_classes < 1) {
      toast.error("Please enter a valid number of classes");
      return;
    }
    setRenewLoading(true);
    try {
      const response = await axios.post(`${API}/students/${id}/renew`, {
        additional_classes: renewData.additional_classes,
        notes: renewData.notes || undefined
      }, { headers: getAuthHeader() });
      toast.success(response.data.message);
      
      // Auto-download receipt PDF via fetch (handles auth + special chars)
      try {
        const params = new URLSearchParams({
          credits_added: String(renewData.additional_classes),
          previous_balance: String(response.data.previous_balance),
          new_balance: String(response.data.new_balance),
          total_purchased: String(response.data.total_purchased),
        });
        if (renewData.notes) params.append('notes', renewData.notes);
        if (response.data.transaction_id) params.append('transaction_id', response.data.transaction_id);
        
        const receiptRes = await fetch(`${API}/students/${id}/receipt?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (receiptRes.ok) {
          const blob = await receiptRes.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `receipt_${student?.name?.replace(/\s+/g, '_') || 'student'}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } else {
          console.error("Receipt generation failed:", receiptRes.status);
        }
      } catch (receiptErr) {
        console.error("Receipt download error:", receiptErr);
      }
      
      setRenewDialogOpen(false);
      setRenewData({ additional_classes: 20, notes: "" });
      fetchData();
    } catch (error) {
      console.error("Failed to renew subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to renew subscription");
    } finally {
      setRenewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="student-detail-loading">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12" data-testid="student-not-found">
        <p className="text-slate-500">Student not found</p>
        <Link to="/students" className="text-indigo-600 hover:underline mt-2 inline-block">
          Back to Students
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-detail-page" ref={pageRef}>
      {/* Back button */}
      <Link
        to="/students"
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        data-testid="back-to-students"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Students
      </Link>

      {/* Student Header */}
      <Card className="border-slate-200 shadow-sm" data-testid="student-header-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-700 font-bold text-xl">
                  {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" data-testid="student-name">
                  {student.name}
                </h1>
                <p className="text-slate-500">
                  {student.total_purchased_classes} classes purchased
                  {student.validity_date && ` • Valid until ${student.validity_date}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-600" data-testid="remaining-classes">
                  {student.remaining_classes}
                </p>
                <p className="text-sm text-slate-500">Remaining</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-400">
                  {student.total_purchased_classes - student.remaining_classes}
                </p>
                <p className="text-sm text-slate-500">Used</p>
              </div>
              <Button
                onClick={() => setRenewDialogOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="renew-subscription-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Renew Subscription
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoom Link Card */}
      <ZoomLinkCard student={student} onUpdate={fetchData} />

      {/* Class Records Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold">Class Records</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportPDF}
              data-testid="export-student-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              onClick={() => setAddDialogOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 btn-active"
              data-testid="add-record-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Record
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600 w-36">Date</TableHead>
                <TableHead className="font-semibold text-slate-600 w-32">Status</TableHead>
                <TableHead className="font-semibold text-slate-600">Notes</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center w-28">Remaining</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    No class records yet. Add one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow
                    key={record.id}
                    className={`table-row-hover ${getStatusStyle(record.status).split(' ')[0]}`}
                    data-testid={`record-row-${record.id}`}
                  >
                    {editingId === record.id ? (
                      <>
                        <TableCell>
                          <Input
                            type="date"
                            value={editData.date}
                            onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                            className="h-8 w-32"
                            data-testid="edit-date-input"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={editData.status}
                            onValueChange={(value) => setEditData({ ...editData, status: value })}
                          >
                            <SelectTrigger className="h-8 w-28" data-testid="edit-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            className="h-8"
                            placeholder="Add notes..."
                            data-testid="edit-notes-input"
                          />
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {record.remaining_after}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSaveEdit(record.id)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid="save-edit-btn"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              className="text-slate-600 hover:text-slate-700"
                              data-testid="cancel-edit-btn"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="font-medium text-slate-900">
                          {record.date}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyle(record.status)}`}
                          >
                            {record.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {record.notes || <span className="text-slate-400 italic">-</span>}
                        </TableCell>
                        <TableCell className="text-center font-medium text-slate-900">
                          {record.remaining_after}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStartEdit(record)}
                              className="text-slate-600 hover:text-indigo-600"
                              data-testid={`edit-record-${record.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-slate-600 hover:text-red-600"
                              data-testid={`delete-record-${record.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Record Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-record-dialog" aria-describedby="add-record-description">
          <DialogHeader>
            <DialogTitle>Add Class Record</DialogTitle>
            <p id="add-record-description" className="text-sm text-slate-500">
              Record a new class session for this student
            </p>
          </DialogHeader>
          <form onSubmit={handleAddRecord}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={newRecord.date}
                  onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                  required
                  data-testid="new-record-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={newRecord.status}
                  onValueChange={(value) => setNewRecord({ ...newRecord, status: value })}
                >
                  <SelectTrigger data-testid="new-record-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  value={newRecord.notes}
                  onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                  placeholder="e.g., With T. Gen"
                  data-testid="new-record-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddDialogOpen(false)}
                data-testid="cancel-add-record-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-add-record-btn"
              >
                Add Record
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-record-dialog" aria-describedby="delete-record-description">
          <DialogHeader>
            <DialogTitle>Delete Record</DialogTitle>
          </DialogHeader>
          <p id="delete-record-description" className="text-slate-600 py-4">
            Are you sure you want to delete the class record for{" "}
            <strong>{selectedRecord?.date}</strong>? This will recalculate remaining classes.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-record-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteRecord}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-record-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Subscription Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="renew-subscription-dialog" aria-describedby="renew-description">
          <DialogHeader>
            <DialogTitle>Renew Subscription</DialogTitle>
            <p id="renew-description" className="text-sm text-slate-500">
              Add more class credits to {student?.name}'s account
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Current Remaining Credits</span>
                <span className="font-bold text-indigo-600">{student?.remaining_classes}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Purchased</span>
                <span className="font-medium text-slate-700">{student?.total_purchased_classes}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="additional-classes">Classes to Add *</Label>
              <Input
                id="additional-classes"
                type="number"
                min="1"
                value={renewData.additional_classes}
                onChange={(e) => setRenewData({ ...renewData, additional_classes: parseInt(e.target.value) || 0 })}
                placeholder="e.g., 20"
                data-testid="renew-classes-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="renew-notes">Notes (Optional)</Label>
              <Input
                id="renew-notes"
                value={renewData.notes}
                onChange={(e) => setRenewData({ ...renewData, notes: e.target.value })}
                placeholder="e.g., Monthly renewal - Feb 2026"
                data-testid="renew-notes-input"
              />
            </div>
            {renewData.additional_classes > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                After renewal: <strong>{student?.remaining_classes} + {renewData.additional_classes} = {(student?.remaining_classes || 0) + renewData.additional_classes} credits</strong>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenewDialogOpen(false)}
              data-testid="cancel-renew-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenewSubscription}
              disabled={renewLoading || !renewData.additional_classes || renewData.additional_classes < 1}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-renew-btn"
            >
              {renewLoading ? "Renewing..." : "Add Credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export PDF Selection Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="export-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <FileDown className="w-5 h-5 text-indigo-600" />
              Export Class Record
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                exportType === "current"
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="export-current-option"
            >
              <input
                type="radio"
                name="export_type"
                value="current"
                checked={exportType === "current"}
                onChange={() => setExportType("current")}
                className="mt-1 accent-indigo-600"
              />
              <div>
                <p className="font-semibold text-slate-800 text-sm">Current Subscription Record</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Only classes since the last subscription renewal.
                </p>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                exportType === "complete"
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="export-complete-option"
            >
              <input
                type="radio"
                name="export_type"
                value="complete"
                checked={exportType === "complete"}
                onChange={() => setExportType("complete")}
                className="mt-1 accent-indigo-600"
              />
              <div>
                <p className="font-semibold text-slate-800 text-sm">Complete Class History</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  All records across every subscription period.
                </p>
              </div>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={doExportPDF}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="export-pdf-confirm-btn"
            >
              <FileDown className="w-4 h-4 mr-1.5" />
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
